import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Platform, PROSPECT_PHONE_TYPE } from 'interfaces/db';
import { AutoDialerSetting, SETTING, SettingValueType, PhonePreference, PhoneKeys, ControlType } from 'interfaces/settings';
import { clearCache, SERVICE_CALLS } from 'extension-helpers';
import {
    setSelectedPlatform,
    resetAdjustedSettings,
    clearError,
    setSelection,
    updateSingleSetting,
    setSaving,
    saveSettingsSuccess,
    saveSettingsFailure,
    getSelectionTypeForSetting
} from 'lib/redux/settings/autodialerSettingsSlice'
import { SelectionType, UserSelection } from "interfaces/settings";
import { getPhoneLabel, SETTING_TO_DEFAULT_VALUE } from '../config/defaultValues';
import { SETTING_TO_CONTROL_TYPE } from '../config/settingMaps';
import { getRangeConfig } from '../SettingsTypes/range/rangeConfig';
import { getSelectConfig } from '../SettingsTypes/categorical/selectConfig';
import { getServicesManager } from 'services';
import { useUserData } from '../../../lib/redux/users/user-data-hook';

// Helper function to format time range
const formatTimeRange = (minHours: number, maxHours: number): string => {
    const formatTime = (hours: number): string => {
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:00 ${period}`;
    };
    return `${formatTime(minHours)} - ${formatTime(maxHours)}`;
};

export function useAutodialerSettings() {
    const dispatch = useDispatch();
    const {
        savedSettings,
        adjustedSettings,
        selectedPlatform,
        isLoaded,
        isSaving,
        error,
        hasUnsavedChanges,
        selection,
    } = useSelector((state: RootState) => state.autodialerSettings);
    
    const { user, isAdmin } = useUserData();
    const userGroupInfo = useSelector((state: RootState) => state.userGroupInfo.sortedGroupInfo);
    const userGroups = useSelector((state: RootState) => state.userGroupInfo.sortedGroupInfo)
    const updateSelectedPlatform = useCallback((platform: Platform | null) => dispatch(setSelectedPlatform(platform)), [dispatch]);
    
    const settingsForPlatform = useMemo(() => { 
        if (!selectedPlatform) return []
        return adjustedSettings.filter(s => s.platform === selectedPlatform)
    }, [selectedPlatform, adjustedSettings])

    const getNonNullSettingsForPlatform = useCallback((setting: SETTING) => {
        return settingsForPlatform.filter(s => s[setting] !== null)
    }, [settingsForPlatform])

    const getPrioritySettingForSelection = (settings: AutoDialerSetting[]): AutoDialerSetting => {
        return settings.sort((a, b) => {
            if (a.locked_user_id !== "") return -1
            if (b.locked_user_id !== "") return 1
            if (a.user_group_id !== "") return -1
            if (b.user_group_id !== "") return 1
            if (a.team_id !== "") return -1
            if (b.team_id !== "") return 1
            return 0
        })[0]
    }

    const getSettingRowForSetting = useCallback((setting: SETTING): AutoDialerSetting | null => {
        if (!selectedPlatform || !selection) return null
        const nonNullSettings = getNonNullSettingsForPlatform(setting)
        // if we are on a user selection, all are eligible
        // if we are on a team selection, only the team settings are eligible
        // if we are on a sub team selection, only the sub team setting for that team and sub team are eligible
        // if we are on a user selection, only the locked user setting for that user or the user setting for that user are eligible
        const eligibleSettings = nonNullSettings.filter(s => {
            switch (selection.type) {
                case SelectionType.ME:
                case SelectionType.USER:
                    return s.user_id === selection.id || 
                    s.locked_user_id === selection.id ||    
                    (s.user_group_id !== '' && userGroups.find((v) => v.allUserIds.includes(selection.id) && v.group.user_group_id === s.user_group_id) !== undefined) ||
                    s.team_id === user?.team_id
                case SelectionType.OVERALL_TEAM:
                    return s.team_id === selection.id
                case SelectionType.SUB_TEAM:
                    return s.user_group_id === selection.id || s.team_id !== ''
            }
            return false
        })
        const prioritySetting = getPrioritySettingForSelection(eligibleSettings)
        return prioritySetting
    }, [selectedPlatform, selection, getNonNullSettingsForPlatform, userGroups, user])

    const updateSelection = useCallback((selection_type: SelectionType, selection_id?: string) => {
        let corresponding_id = null
        switch (selection_type) {
            case SelectionType.ME: corresponding_id = user?.user_id; break;
            case SelectionType.OVERALL_TEAM: corresponding_id = user?.team_id; break;
            case SelectionType.SUB_TEAM: corresponding_id = userGroupInfo?.find(g => g.group.user_group_id === selection_id)?.group.user_group_id; break;
            case SelectionType.USER: corresponding_id = selection_id; break;
        }
        if (corresponding_id) dispatch(setSelection({ type: selection_type, id: corresponding_id }));
    }, [dispatch, userGroupInfo, user]);

    const getValueForSetting = useCallback((setting: SETTING): SettingValueType<SETTING> | null => {
        const settingRow = getSettingRowForSetting(setting)
        if (!settingRow) return null
        return settingRow[setting] as SettingValueType<SETTING>
    }, [getSettingRowForSetting])

    const getSettingSetBySelection = useCallback((setting: SETTING): UserSelection | null => {
        const settingRow = getSettingRowForSetting(setting)
        if (!settingRow) return null
        if (settingRow.locked_user_id !== "") return { type: SelectionType.USER, id: settingRow.locked_user_id }
        if (settingRow.user_group_id !== "") return { type: SelectionType.SUB_TEAM, id: settingRow.user_group_id }
        if (settingRow.team_id !== "") return { type: SelectionType.OVERALL_TEAM, id: settingRow.team_id }
        return { type: SelectionType.ME, id: settingRow.user_id }
    }, [getSettingRowForSetting])

    const getSettingIsEditable = useCallback((setting: SETTING): boolean => {
        if (isAdmin) return true
        const settingSet = getSettingSetBySelection(setting)
        return !settingSet || settingSet.type === SelectionType.ME
    }, [getSettingSetBySelection, isAdmin])

    const updateSetting = useCallback((
        setting: SETTING,
        value: SettingValueType<SETTING> | null,
    ) => {
        if (!selectedPlatform || !selection) return
        let selectionToUse = selection
        if (selection.type === SelectionType.ME) {
            const canEditCurrentSetting = getSettingIsEditable(setting)
            if (!canEditCurrentSetting) return
            // if it's currently set by a different selection, we need to use the locked user id
            const settingSet = getSettingSetBySelection(setting)
            if (settingSet && settingSet.type !== SelectionType.ME) selectionToUse = { type: SelectionType.USER, id: selection.id }
        }
        dispatch(updateSingleSetting({
            platform: selectedPlatform,
            setting,
            value,
            selection: selectionToUse,
        }))
    }, [dispatch, selectedPlatform, selection, getSettingIsEditable, getSettingSetBySelection])

    const settingsAreMatching = useCallback((setting1: AutoDialerSetting, setting2: AutoDialerSetting) => {
        return setting1.platform === setting2.platform && JSON.stringify(getSelectionTypeForSetting(setting1)) === JSON.stringify(getSelectionTypeForSetting(setting2))
    }, [])

    const saveSettings = useCallback(async () => {
        dispatch(setSaving(true))
        try {
            for (const adjustedSetting of adjustedSettings) {
                const matchingSavedSetting = savedSettings.find(s => settingsAreMatching(s, adjustedSetting))
                let hasChanges = false
                if (!matchingSavedSetting) hasChanges = true;
                else hasChanges = JSON.stringify(matchingSavedSetting) !== JSON.stringify(adjustedSetting)
                if (hasChanges) {
                    const selection = getSelectionTypeForSetting(adjustedSetting)
                    const success = await getServicesManager().putAutodialerSettings(adjustedSetting, selection);
                    if (!success) throw new Error('Failed to save setting');
                }
            }
            clearCache(SERVICE_CALLS.AUTODIALER_SETTINGS)
            dispatch(saveSettingsSuccess())
        } catch (error) {
            dispatch(saveSettingsFailure(error instanceof Error ? error.message : 'Failed to save settings'));
        } finally {
            dispatch(setSaving(false))
        }
    }, [dispatch, savedSettings, adjustedSettings, settingsAreMatching]);

    const resetSettings = useCallback(() => {
        dispatch(resetAdjustedSettings());
    }, [dispatch]);

    const dismissError = useCallback(() => {
        dispatch(clearError())
    }, [dispatch]);

    const getSettingSetByDifferentSelection = useCallback((setting: SETTING): boolean => {
        if (!selectedPlatform || !selection) return false
        const settingSet = getSettingSetBySelection(setting)
        if (settingSet && selection.type === SelectionType.ME && isAdmin && settingSet.type === SelectionType.USER && settingSet.id === selection.id) return false
        return settingSet !== null && settingSet.type !== selection.type
    }, [getSettingSetBySelection, selectedPlatform, selection, isAdmin])

    const formatSettingValue = useCallback((setting: SETTING, value: SettingValueType<SETTING>): string => {
        if (value === null || value === undefined) return '';
        const controlType = SETTING_TO_CONTROL_TYPE[setting];
        switch (controlType) {
            case ControlType.RANGE:
                const rangeConfig = getRangeConfig(setting);
                return `${value}${rangeConfig.unit}`;
            case ControlType.TOGGLE:
                return value ? 'Enabled' : 'Disabled';
            case ControlType.SELECT:
                const selectConfig = getSelectConfig(setting);
                const option = selectConfig.options.find(opt => opt.value === value);
                return option ? option.label : value?.toString();
            case ControlType.CUSTOM:
                // Special handling for PHONE_PREFERENCE
                if (setting === SETTING.PHONE_PREFERENCE) {
                    const phonePreferenceMap = value as Record<PROSPECT_PHONE_TYPE, PhonePreference>;
                    if (!phonePreferenceMap || Object.keys(phonePreferenceMap).length === 0) return '';
                    
                    // Get active phone types and sort by order
                    const activeTypes: Array<{ type: PROSPECT_PHONE_TYPE; order: number; doubleTaps: number }> = [];
                    const inactiveTypes: Array<{ type: PROSPECT_PHONE_TYPE; order: number }> = [];
                    
                    Object.entries(phonePreferenceMap).forEach(([phoneType, preference]) => {
                        if (preference[PhoneKeys.ACTIVE]) {
                            activeTypes.push({
                                type: phoneType as PROSPECT_PHONE_TYPE,
                                order: preference[PhoneKeys.ORDER],
                                doubleTaps: preference[PhoneKeys.DOUBLE_TAPS]
                            });
                        } else {
                            inactiveTypes.push({
                                type: phoneType as PROSPECT_PHONE_TYPE,
                                order: preference[PhoneKeys.ORDER]
                            });
                        }
                    });
                    activeTypes.sort((a, b) => a.order - b.order);
                    inactiveTypes.sort((a, b) => a.order - b.order);
                    const totalTypes = activeTypes.length + inactiveTypes.length;
                    let summary = '';
                    if (activeTypes.length === 0) summary = 'Will call no phone types';
                    else if (activeTypes.length === totalTypes) summary = 'Will call all phone types';
                    else summary = `Will call ${activeTypes.length} of ${totalTypes} phone types`;
                    if (activeTypes.length > 0) summary += ` in order: ${activeTypes.map(t => getPhoneLabel(t.type, selectedPlatform)).join(', ')}`;
                    const doubleTapTypes = activeTypes.filter(t => t.doubleTaps > 1)
                    if (doubleTapTypes.length > 0) summary += `, double-tapping ${doubleTapTypes.length} types (up to ${Math.max(...doubleTapTypes.map(t => t.doubleTaps))} taps)`
                    return summary;
                }
                
                // Special handling for LOCAL_CALL_TIME settings
                if (setting === SETTING.LOCAL_CALL_TIME_MIN && selectedPlatform) {
                    const minValue = value as number;
                    const maxValue = SETTING_TO_DEFAULT_VALUE[SETTING.LOCAL_CALL_TIME_MAX](selectedPlatform) as number;
                    return formatTimeRange(minValue, maxValue);
                }
                if (setting === SETTING.LOCAL_CALL_TIME_MAX && selectedPlatform) {
                    const minValue = SETTING_TO_DEFAULT_VALUE[SETTING.LOCAL_CALL_TIME_MIN](selectedPlatform) as number;
                    const maxValue = value as number;
                    return formatTimeRange(minValue, maxValue);
                }
                return 'Custom setting';
            default:
                return value?.toString() || '';
        }
    }, [selectedPlatform])

    const getDefaultValueForSetting = useCallback((setting: SETTING): SettingValueType<SETTING> | null => {
        if (!selectedPlatform) return null
        return SETTING_TO_DEFAULT_VALUE[setting](selectedPlatform)
    }, [selectedPlatform])

    const getSettingIsLocked = useCallback((setting: SETTING): boolean => {
        if (!selectedPlatform) return false
        return true
    }, [selectedPlatform])

    return {
        // State
        savedSettings,
        adjustedSettings,
        selectedPlatform,
        isLoaded,
        isSaving,
        error,
        hasUnsavedChanges,
        selection,
        // Actions
        updateSelectedPlatform,
        updateSetting,
        saveSettings,
        resetSettings,
        dismissError,
        updateSelection,
        
        // Helpers
        getSettingIsEditable,
        getSettingIsLocked,
        getValueForSetting,
        getDefaultValueForSetting,
        getSettingSetByDifferentSelection,
        getSettingSetBySelection,
        formatSettingValue,

        // temporary
        getSettingRowForSetting
    };
} 